import {
	arrow,
	autoUpdate,
	flip,
	FloatingArrow,
	FloatingPortal,
	offset,
	type Placement,
	safePolygon,
	shift,
	useClick,
	useDismiss,
	useFloating,
	useFocus,
	useHover,
	useInteractions,
	useMergeRefs,
	useRole,
	useTransitionStyles,
} from '@floating-ui/react';
import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';

import './BayTooltip.scss';

/*
 * BayTooltip -- the tooltip, on the same engine as BayFloatManager.
 *
 * Phase 3 of .plan/feat-overlay-consolidation. This replaced `BayRcTooltip`
 * (rc-tooltip, the old engine), which is deleted -- along with the dependency. It
 * took the `BayTooltip` name, so all 331 consumer files and 124 bay-ui internals
 * resolve here BY PATH with no call-site edits.
 *
 * WHY A SEPARATE COMPONENT FROM BayFloatManager
 * ----------------------------------------
 * A tooltip is the opposite problem from a dialog. BayFloatManager does NOT position --
 * it uses `useFloating` for context only, because a modal is not anchored to
 * anything and its placement is CSS. A tooltip is nothing BUT anchoring, and it has
 * no backdrop, no focus trap and no scroll lock. Sharing the chrome would mean one
 * component with two disjoint halves. They share the library, not the component.
 *
 * THE PROP SURFACE IS BayTooltip'S, DELIBERATELY
 * ---------------------------------------------
 * 331 files consume BayTooltip (client 134, smc 50, ui-design 23) plus 124 more
 * inside bay-ui, and nothing outside bay-ui imports rc-tooltip directly. So the
 * props here are a match for the old ones, down to delays being FLOAT SECONDS,
 * so migration is an import swap rather than a rewrite. Two exceptions are called
 * out on the props themselves: `alignConfig` and `animation`/`motion`.
 *
 * WHAT THIS FIXES ON THE WAY THROUGH
 * ----------------------------------
 * 1. `zIndex={9999}` becomes `var(--bs-zindex-tooltip, 1080)`. The old literal is
 *    exactly what docs/css-unification/overlay-and-zindex.md exists to stop.
 * 2. The arrow is `FloatingArrow` -- one SVG whose colour is a `fill` prop. The old
 *    arrow was a CSS border triangle, which is why colouring it needed a rule PER
 *    PLACEMENT (`.rc-tooltip-placement-topRight .rc-tooltip-arrow { border-top-color
 *    }` and five siblings) and why client and smc each carry copies of those rules.
 *    One `fill` replaces all of them.
 * 3. `safePolygon()` replaces the `mouseLeaveDelay: 0.1` grace window. That default
 *    existed only so the cursor could cross the gap between trigger and popup
 *    without the tooltip hiding -- see the long comment in BayRcTooltip/index.jsx
 *    about the blink it caused when someone set it to 0. safePolygon computes the
 *    actual travel area instead of guessing a duration, so the delay stops being
 *    load-bearing.
 * 4. Own class namespace (`bay-tooltip-*`). No `rc-*` names, so no consuming app's
 *    stylesheet can reach in by accident -- and the 33 places across the three repos
 *    that currently target `.rc-tooltip*` become a visible, greppable migration
 *    instead of an invisible dependency.
 *
 * NO INLINE STYLE EXCEPT WHERE IT IS THE ONLY OPTION
 * -------------------------------------------------
 * Two things are inline and both are unavoidable: Floating UI's computed x/y (a
 * value that changes on every scroll cannot live in a stylesheet) and the
 * transition, which Floating UI owns. `overlayMaxWidth` is dynamic, so it sets the
 * custom property `--bay-tooltip-max-width` and the stylesheet owns the real
 * `max-width` -- the `--bay-pdf-viewer-height` pattern. Everything else is in
 * BayTooltip.scss.
 */

// rc-tooltip's placement vocabulary -> Floating UI's. 477 call sites pass one of
// these strings, so the mapping is the compatibility surface, not a convenience.
// Floating UI's `-start`/`-end` are writing-mode aware where rc's Left/Right are
// literal; for our LTR-only apps they resolve the same.
const PLACEMENTS: Record<string, Placement> = {
	top: 'top',
	topLeft: 'top-start',
	topRight: 'top-end',
	bottom: 'bottom',
	bottomLeft: 'bottom-start',
	bottomRight: 'bottom-end',
	left: 'left',
	leftTop: 'left-start',
	leftBottom: 'left-end',
	right: 'right',
	rightTop: 'right-start',
	rightBottom: 'right-end',
};

// Also accept Floating UI's own names, so new code is not forced to write rc's.
const NATIVE_PLACEMENTS = new Set<string>(Object.values(PLACEMENTS));

// Arrow and gap geometry, matched to rc-tooltip's published values so the new engine
// lands where the old one did. Read from the shipped stylesheet and placement table
// rather than eyeballed:
//
//   rc-tooltip/assets/bootstrap.css   .rc-tooltip-arrow { border-width: 5px 5px 0 }
//                                     -> a 5px-tall, 10px-wide triangle
//   rc-tooltip/lib/placements.js      offset: [0, -4] on every placement
//
// `offset(ARROW_HEIGHT + GAP)` is the distance from the trigger to the PANEL, and
// FloatingArrow occupies ARROW_HEIGHT of it, so the visible trigger-to-arrow-tip gap
// is GAP -- the same 4px rc used.
const ARROW_HEIGHT = 5;
const ARROW_WIDTH = 10;
const GAP = 4;

const TRANSITION_MS = 100;

/** A bare number means px; anything else is already a CSS length. */
const toCssLength = (value: number | string) =>
	typeof value === 'number' ? `${value}px` : value;

/**
 * Can React attach a ref to this element? Only host tags, forwardRef components and
 * class components can. Function components cannot, and many icon libraries --
 * react-icons among them -- are plain function components.
 *
 * This matters because rc-trigger fell back to `findDOMNode` for children it could
 * not ref, so `<BayTooltip><SomeIcon /></BayTooltip>` worked by accident. findDOMNode
 * is gone in React 19 and was never something to rely on, so instead of failing
 * silently (no ref means no measurement means the tooltip positions at 0,0) we wrap
 * the child in a span. The wrap is the exception; cloning is the normal path,
 * because an extra element can change layout inside a flex or grid parent.
 */
const canHoldRef = (element: React.ReactElement): boolean => {
	const type: any = element?.type;
	if (typeof type === 'string') return true;
	if (type?.$$typeof === Symbol.for('react.forward_ref')) return true;
	return typeof type === 'function' && Boolean(type.prototype?.isReactComponent);
};

export interface BayTooltipProps {
	/** The trigger. Cloned when it can take a ref, wrapped in a span when it cannot. */
	children: React.ReactElement;
	/** Tooltip content. Nothing renders when this is null or undefined. */
	overlay?: React.ReactNode;
	/** Merged onto the popup, never spread after it -- a spread would replace `bay-tooltip`. */
	overlayClassName?: string;
	/**
	 * Accepts rc-tooltip's vocabulary (`top`, `topLeft`, `bottomRight`, `leftTop`, ...)
	 * so existing call sites need no edit, and Floating UI's own (`top-start`) for new
	 * code. An unrecognised value falls back to `top` rather than positioning at 0,0.
	 */
	placement?: string;
	/** Optional id forwarded to the tooltip element. */
	id?: string;
	/** Optional class forwarded to the tooltip wrapper. */
	className?: string;
	/** FLOAT SECONDS, as in BayTooltip. Converted to ms internally. */
	mouseEnterDelay?: number;
	/** FLOAT SECONDS. Largely redundant now that safePolygon handles the travel gap. */
	mouseLeaveDelay?: number;
	trigger?: 'hover' | 'click' | 'focus' | (string & {});
	/** Renders the trigger bare. 112 call sites rely on this. */
	disabled?: boolean;
	/**
	 * `{ visible, onVisibleChange }`, BayTooltip's shape. Prefer leaving it
	 * uncontrolled: controlling visibility re-renders the trigger on every hover.
	 */
	controlVisibilityProps?: {
		visible?: boolean;
		onVisibleChange?: (visible: boolean) => void;
		[key: string]: any;
	};
	/** Light popup on dark text instead of the default dark on light. */
	inverseStyle?: boolean;
	/** Number (px) or any CSS length. A CEILING -- see `overlayWidth` for a fixed width. */
	overlayMaxWidth?: number | string;
	/** FIXED width, where `overlayMaxWidth` is only a ceiling. Unset means content-sized. */
	overlayWidth?: number | string;
	/** Visible px between the trigger and the arrow tip. Defaults to 4 (rc-tooltip's value). */
	arrowOffset?: number;
	/** Let the tooltip move to the opposite side when the preferred one does not fit. */
	allowFlip?: boolean;
	/** Close when any scrollable ancestor of the trigger scrolls, e.g. a dropdown option list. */
	closeOnScroll?: boolean;
	/** NOT SUPPORTED -- accepted so call sites passing it do not crash, then ignored. */
	alignConfig?: any;
	/** Ignored. This component owns its transition; rc's motion names mean nothing here. */
	animation?: string | boolean;
	/** Ignored. Same as `animation`. */
	motion?: string | boolean;
	/** Escape hatch for a genuinely dynamic value. Prefer `overlayClassName`. */
	overlayStyle?: React.CSSProperties;
}

const BayTooltip = ({
	children,
	overlay,
	overlayClassName,
	placement = 'top',
	// FLOAT SECONDS, matching BayTooltip. Floating UI's `useHover` takes
	// milliseconds, so these are multiplied below -- passing 0.15 straight through
	// would be 0.15ms, i.e. no delay at all, and would look like the prop being
	// ignored rather than misread.
	mouseEnterDelay = 0.15,
	mouseLeaveDelay = 0.1,
	trigger = 'hover',
	disabled = false,
	controlVisibilityProps = {},
	inverseStyle = false,
	overlayMaxWidth = 280,
	overlayWidth,
	arrowOffset = GAP,
	allowFlip = true,
	closeOnScroll = false,
	// Accepted and ignored -- see the props. Renamed with a leading underscore so the
	// no-unused-vars rule records them as intentionally unused rather than needing a
	// disable comment: they must stay in the signature so a caller passing them does
	// not have them land in a `...rest` spread onto the DOM.
	alignConfig: _alignConfig = null,
	animation: _animation,
	motion: _motion,
	overlayStyle,
}: BayTooltipProps) => {
	const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
	const [isMobileView, setIsMobileView] = useState(false);
	const arrowRef = useRef<SVGSVGElement>(null);

	// `controlVisibilityProps` is BayTooltip's shape: { visible, onVisibleChange }.
	// Only 8 sites use it, and bay-ui/CLAUDE.md warns that controlling an overlay's
	// visibility is what made SideNav's MenuToggle remount mid-hover -- so this stays
	// opt-in and uncontrolled is the default.
	const isControlled = typeof controlVisibilityProps.visible === 'boolean';
	const open = isControlled ? controlVisibilityProps.visible : uncontrolledOpen;
	const setOpen = (next: boolean) => {
		if (!isControlled) setUncontrolledOpen(next);
		controlVisibilityProps.onVisibleChange?.(next);
	};

	// Preserved from BayTooltip exactly: below 992px a hover tooltip is unreachable,
	// because a touch device has no hover, so it is suppressed entirely rather than
	// left to fire on tap. Click and focus triggers are deliberate gestures and stay.
	useEffect(() => {
		const query = window.matchMedia('(max-width: 991px)');
		const onChange = (event: MediaQueryListEvent | MediaQueryList) =>
			setIsMobileView(event.matches);

		onChange(query);
		query.addEventListener('change', onChange);
		return () => query.removeEventListener('change', onChange);
	}, []);

	const resolvedPlacement: Placement = NATIVE_PLACEMENTS.has(placement)
		? (placement as Placement)
		: (PLACEMENTS[placement] ?? 'top');

	const { refs, floatingStyles, context } = useFloating({
		open,
		onOpenChange: setOpen,
		// automatically update the tooltip position during mounting when there isn't enough space to display it in the intended position.
		whileElementsMounted: autoUpdate,
		placement: resolvedPlacement,
		// LEFT/TOP, not `transform`. Floating UI defaults to
		// `transform: translate(x, y)` with `left: 0; top: 0`, which is marginally cheaper
		// to animate but makes the position UNREACHABLE from a stylesheet: overriding `top`
		// does nothing when the real offset is inside a transform, and overriding the
		// transform throws away the horizontal position with it.
		//
		// rc-tooltip positioned with `left`/`top`, and the estate has stylesheet rules
		// written against that -- SideNav's collapse handle is the load-bearing one. Its
		// trigger is a 62px x 24px hit strip whose glyph sits at `left: -21px; top: 7px`
		// inside it, so the visible chevron is not centred on its own trigger in either
		// axis and no `placement` lands the tooltip correctly. Those rules have to be able
		// to state a coordinate, so this has to be `false`.
		transform: false,
		middleware: [
			// Clear the arrow, or it overlaps the trigger. `arrowOffset` is the visible
			// part; ARROW_HEIGHT is consumed by the arrow itself.
			offset(ARROW_HEIGHT + arrowOffset),
			// `crossAxis: false` is load-bearing, not a tweak. It DEFAULTS TO TRUE
			// (verified in @floating-ui/core's flip: `crossAxis: checkCrossAxis = true`),
			// which means flip also changes the placement when the tooltip overflows
			// SIDEWAYS -- and sideways is `shift()`'s job, right below.
			//
			// With both left on, the two fight and shift never gets a turn: a trigger
			// near the left edge of the viewport made `placement="top"` flip to `right`,
			// because the centred tooltip would have hung off the left edge. There was
			// plenty of room above; nothing about the main axis had failed. Floating UI's
			// own guidance is to set this to false (or 'alignment') whenever shift() is
			// in use, for exactly this reason.
			//
			// Now: overflow sideways -> shift slides it back into view and the placement
			// is preserved. No room on the main axis -> flip to the opposite side.
			// Neither side fits -> `fallbackAxisSideDirection` allows the perpendicular
			// axis as a last resort, which is the behaviour worth keeping.
			// `allowFlip={false}` drops this entirely, for the rare caller that needs a
			// placement HELD rather than kept on screen -- see the prop docs. `shift()`
			// still runs, so such a tooltip is nudged into view rather than abandoned.
			allowFlip && flip({ crossAxis: false, fallbackAxisSideDirection: 'start' }),
			// Handles all cross-axis overflow, per the note above.
			shift({ padding: 8 }),
			arrow({ element: arrowRef }),
		],
	});

	const hover = useHover(context, {
		enabled: trigger === 'hover',
		move: false,
		delay: { open: mouseEnterDelay * 1000, close: mouseLeaveDelay * 1000 },
		// The real fix for trigger-to-popup travel. safePolygon grants a moving
		// cursor a corridor towards the popup, so the tooltip does not hide the
		// instant the pointer leaves the trigger. This is what `mouseLeaveDelay`
		// was standing in for.
		handleClose: safePolygon(),
	});
	const focus = useFocus(context, { enabled: trigger === 'hover' || trigger === 'focus' });
	const click = useClick(context, { enabled: trigger === 'click' });
	const dismiss = useDismiss(context, { ancestorScroll: closeOnScroll });
	const role = useRole(context, { role: 'tooltip' });

	const { getReferenceProps, getFloatingProps } = useInteractions([
		hover,
		focus,
		click,
		dismiss,
		role,
	]);

	const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
		duration: TRANSITION_MS,
		initial: { opacity: 0, transform: 'scale(0.96)' },
		open: { opacity: 1, transform: 'scale(1)' },
	});

	const childRef = canHoldRef(children) ? (children as any).ref : null;
	const referenceRef = useMergeRefs([refs.setReference, childRef]);

	const clickableTriggers = trigger === 'click' || trigger === 'focus';
	const shouldRender = !disabled && overlay != null && (!isMobileView || clickableTriggers);

	if (!shouldRender) return children;

	const referenceProps = getReferenceProps({ ref: referenceRef });
	const anchor = canHoldRef(children) ? (
		React.cloneElement(children, referenceProps)
	) : (
		// Fallback for a child that cannot take a ref -- see canHoldRef.
		<span className="bay-tooltip-trigger" {...referenceProps}>
			{children}
		</span>
	);

	return (
		<>
			{anchor}
			{isMounted && (
				<FloatingPortal>
					{/*
					 * TWO ELEMENTS, AND THEY CANNOT BE MERGED.
					 *
					 * Floating UI POSITIONS WITH `transform`. `floatingStyles` is
					 * `{position, left: 0, top: 0, transform: translate(x, y)}` -- the
					 * translate IS the position; left/top stay at 0.
					 *
					 * So the positioned element cannot also be the animated one. Putting
					 * both style objects on one node means the transition's
					 * `transform: scale(...)` overwrites the translate, the tooltip loses
					 * its position entirely and renders at the portal's top-left corner
					 * for every placement. That is exactly what happened on the first
					 * version of this component, and it reads as "placement is broken"
					 * rather than as a clobbered style.
					 *
					 * The outer node is Floating UI's alone: NOTHING else may set
					 * `transform` on it. The inner node carries the transition and every
					 * visual style.
					 *
					 * BayFloatManager does not have this problem because it never applies
					 * `floatingStyles` -- it uses the context only, and its placement is
					 * CSS. This is the one place the distinction bites.
					 */}
					{/*
					 * `zIndex` from `overlayStyle` is HOISTED to this element and must stay
					 * hoisted. The positioner is what carries `z-index` (BayTooltip.scss) and
					 * therefore what establishes the stacking context; the panel below is its
					 * CHILD. A `zIndex` set on the panel is trapped inside that context and
					 * cannot lift the tooltip past anything -- it is silently inert.
					 *
					 * rc-tooltip had one node, so `overlayStyle={{ zIndex }}` landed on the
					 * element that owned the z-index and worked. Splitting positioner from panel
					 * (required, see the transform note above) broke that contract for every
					 * caller using overlayStyle to escape a high-z-index container.
					 *
					 * Real case: the email editor's MergeTagInlinePopover renders at
					 * `zIndex: 99999` and passes `overlayStyle={{ zIndex: 100100 }}` to lift its
					 * help tooltips above itself. That stopped working on migration -- the
					 * tooltip rendered behind the popover.
					 *
					 * The rest of `overlayStyle` stays on the panel, where it styles the visible
					 * box as it always did.
					 */}
					<div
						ref={refs.setFloating}
						className="bay-tooltip-positioner"
						style={
							overlayStyle?.zIndex == null
								? floatingStyles
								: { ...floatingStyles, zIndex: overlayStyle.zIndex }
						}
						{...getFloatingProps()}
					>
						<div
							className={clsx('bay-tooltip', inverseStyle && 'bay-tooltip-inverse', overlayClassName)}
							style={{
								// Owned by Floating UI. Safe here: this node is not positioned.
								...transitionStyles,
								// Dynamic value, so it sets a custom property and BayTooltip.scss owns
								// the real `max-width`. Same pattern as --bay-pdf-viewer-height.
								'--bay-tooltip-max-width': toCssLength(overlayMaxWidth),
								// Left undefined when not passed, so the stylesheet's `auto` fallback applies
								// and a content-sized tooltip stays content-sized.
								'--bay-tooltip-width': overlayWidth == null ? undefined : toCssLength(overlayWidth),
								// Escape hatch, kept because BayTooltip had it. Last so a caller
								// can still win.
								...overlayStyle,
							} as React.CSSProperties}
						>
							{overlay}
							<FloatingArrow
								ref={arrowRef}
								className="bay-tooltip-arrow"
								context={context}
								// One `fill` for every placement. The old border-triangle arrow
								// needed a colour rule per placement, which is why client and smc
								// both carry copies of those rules.
								fill="var(--bay-tooltip-bg-color)"
								height={ARROW_HEIGHT}
								width={ARROW_WIDTH}
							/>
						</div>
					</div>
				</FloatingPortal>
			)}
		</>
	);
};

export default BayTooltip;
